import * as Sentry from '@sentry/react';
import { shouldCaptureHttpError, shouldReportToSentry } from './errorFilters';

/**
 * Report an exception to Sentry; skips noise filtered by {@link shouldReportToSentry}.
 */
export function captureException(error, captureContext) {
  if (!shouldReportToSentry(error)) return;
  Sentry.captureException(error, captureContext);
}

/** Axios/API failures — skips 4xx and client connectivity noise (see shouldCaptureHttpError). */
export function captureHttpErrorIfNeeded(error, captureContext) {
  if (!shouldCaptureHttpError(error)) return;
  Sentry.captureException(error, captureContext);
}

/**
 * Report a caught value to Sentry (preferred over ad-hoc console logging for errors).
 * Skips abort/cancel errors. Wraps non-Error values for readable grouping.
 * @param {unknown} caught
 * @param {string} [context] - Short label (Sentry tag `context`)
 * @param {import('@sentry/types').CaptureContext} [captureContext]
 */
export function reportError(caught, context, captureContext = {}) {
  if (caught === undefined || caught === null) {
    if (context) {
      captureMessage(context, { level: 'error', ...captureContext });
    }
    return;
  }
  if (!shouldReportToSentry(caught)) return;
  const tags = { ...captureContext.tags, ...(context ? { context } : {}) };
  if (caught instanceof Error) {
    captureException(caught, { ...captureContext, tags });
    return;
  }
  const detail = safeDetail(caught);
  const message = context ? `${context}: ${detail}` : detail;
  captureException(new Error(message), {
    ...captureContext,
    tags,
    extra: { ...captureContext.extra, original: caught },
  });
}

function safeDetail(caught) {
  try {
    return typeof caught === 'object' && caught !== null
      ? JSON.stringify(caught)
      : String(caught);
  } catch {
    return '[unserializable]';
  }
}

/**
 * Report a non-fatal caught value to Sentry at warning level (use in catch blocks instead of console.warn).
 * Skips abort/cancel errors.
 * @param {unknown} caught
 * @param {string} [context]
 * @param {import('@sentry/types').CaptureContext} [captureContext]
 */
export function reportWarning(caught, context, captureContext = {}) {
  if (caught === undefined || caught === null) {
    if (context) {
      captureMessage(context, { level: 'warning', ...captureContext });
    }
    return;
  }
  if (!shouldReportToSentry(caught)) return;
  const tags = { ...captureContext.tags, ...(context ? { context } : {}) };
  if (caught instanceof Error) {
    captureMessage(`${context ? `${context}: ` : ''}${caught.message}`, {
      level: 'warning',
      ...captureContext,
      tags,
      extra: { ...captureContext.extra, stack: caught.stack, name: caught.name },
    });
    return;
  }
  const message = context ? `${context}: ${safeDetail(caught)}` : safeDetail(caught);
  captureMessage(message, {
    level: 'warning',
    ...captureContext,
    tags,
    extra: { ...captureContext.extra, original: caught },
  });
}

/**
 * Report a message (e.g. warnings). Second argument is passed to Sentry.captureMessage.
 * @param {string} message
 * @param {Record<string, unknown>} [options]
 */
export function captureMessage(message, options = {}) {
  if (typeof message !== 'string' || !message) return;
  if (!shouldReportToSentry({ message }, message)) return;
  Sentry.captureMessage(message, options);
}

/**
 * Report an informational string to Sentry (use in catch instead of console.log for non-error notes).
 * For thrown values / failures, prefer {@link reportError} or {@link reportWarning}.
 */
export function reportInfo(message, captureContext = {}) {
  if (typeof message !== 'string' || !message) return;
  captureMessage(message, { level: 'info', ...captureContext });
}

export function addBreadcrumb(breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

/** Matrix / chat paths — same behavior as captureException (abort filtering). */
export function captureChatException(error, captureContext) {
  captureException(error, captureContext);
}

export function captureChatMessage(message, level = 'warning', captureContext = {}) {
  // Info/debug chatter must not create Sentry issues — use addBreadcrumb for expected paths.
  if (level === 'info' || level === 'debug' || level === 'log') return;
  captureMessage(message, { level, ...captureContext });
}
